import BaseService from './BaseService';
import AppStorage from '../storage/AppStorage';
import type { VisibilityConfig } from '../storage/AppStorage';
import eye00 from '../icons/eye_00.svg';
import eye01 from '../icons/eye_01.svg';
import eye10 from '../icons/eye_10.svg';
import eye11 from '../icons/eye_11.svg';

/**
 * Estado que reciben los handlers cuando cambia la visibilidad.
 */
export interface VisibilityChange extends VisibilityConfig {
  /** URL del icono del ojo correspondiente al estado actual. */
  icon: string;
}

export type VisibilityChangeHandler = (payload: VisibilityChange) => void;

/**
 * Controls visibility flags for map elements.
 *
 * - Hotels / accommodations
 * - POIs
 *
 * Setters accept boolean values.
 * Getters return `'1'` / `'0'` strings (for URL params, storage, etc).
 */
class VisibilityService extends BaseService {
  static showHotels = true;

  static showPOIs = true;

  /**
   * Change handlers fired when visibility changes.
   */
  static changeHandlers = new Set<VisibilityChangeHandler>();

  /**
   * Register a change handler.
   */
  static addChangeHandler(handler: VisibilityChangeHandler): void {
    if (typeof handler === 'function') this.changeHandlers.add(handler);
  }

  /**
   * Unregister a change handler.
   */
  static removeChangeHandler(handler: VisibilityChangeHandler): void {
    this.changeHandlers.delete(handler);
  }

  /**
   * Backward-compatible setter (keeps only one handler).
   * @deprecated Prefer addChangeHandler/removeChangeHandler
   */
  static setChangeHandler(handler: VisibilityChangeHandler): void {
    this.changeHandlers.clear();
    if (typeof handler === 'function') this.changeHandlers.add(handler);
  }

  static notifyChange(): void {
    if (this.changeHandlers.size) {
      const payload: VisibilityChange = {
        showHotels: this.showHotels,
        showPOIs: this.showPOIs,
        icon: this.getIcon(),
      };
      this.changeHandlers.forEach((handler) => {
        try {
          handler(payload);
        } catch {
          // ignore handler errors
        }
      });
    }
  }

  /**
   * @param skipStorage Si es true, no guarda en storage (útil para inicialización)
   */
  static setShowHotels(value: boolean, skipStorage = false): void {
    const next = !!value;
    if (this.showHotels === next) return;
    this.showHotels = next;
    if (!skipStorage) {
      this.saveVisibilityConfig();
    }
    this.notifyChange();
  }

  /**
   * @returns '1' if visible, otherwise '0'
   */
  static getShowHotels(): '1' | '0' {
    return this.showHotels ? '1' : '0';
  }

  /**
   * @param skipStorage Si es true, no guarda en storage (útil para inicialización)
   */
  static setShowPOIs(value: boolean, skipStorage = false): void {
    const next = !!value;
    if (this.showPOIs === next) return;
    this.showPOIs = next;
    if (!skipStorage) {
      this.saveVisibilityConfig();
    }
    this.notifyChange();
  }

  /**
   * @returns '1' if visible, otherwise '0'
   */
  static getShowPOIs(): '1' | '0' {
    return this.showPOIs ? '1' : '0';
  }

  /**
   * Returns the icon path based on current visibility state.
   *
   * Format: src/icons/eye_{hotels}{pois}.svg (imported as an URL by Vite)
   */
  static getIcon(): string {
    const hotels = this.getShowHotels();
    const pois = this.getShowPOIs();
    const key = `${hotels}${pois}`;
    const map: Record<string, string> = {
      '00': eye00,
      '01': eye01,
      '10': eye10,
      '11': eye11,
    };
    return map[key] || eye11;
  }

  /**
   * Saves current visibility configuration to storage.
   * @private
   */
  static saveVisibilityConfig(): void {
    const config: VisibilityConfig = {
      showHotels: this.showHotels,
      showPOIs: this.showPOIs,
    };
    AppStorage.setVisibilityConfig(config);
  }

  /**
   * Initialize visibility state from storage.
   * Should be called at application startup.
   */
  static init(): void {
    const config = AppStorage.getVisibilityConfig();

    // Usar skipStorage=true para evitar guardar durante la inicialización
    this.setShowHotels(config.showHotels, true);
    this.setShowPOIs(config.showPOIs, true);

    // Notificar el cambio una sola vez después de cargar ambos valores
    this.notifyChange();
  }
}

export default VisibilityService;
